import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import RequireAuth from "./components/RequireAuth";
import RequireAdmin from "./components/RequireAdmin";
import Login from "./pages/Login";
import Register from "./pages/Register";
import Walkthrough from "./pages/Walkthrough";
import Verify2FA from "./pages/Verify2FA";
import VerifyEmail from "./pages/VerifyEmail";
import Dashboard from "./pages/Dashboard";
import UploadFile from "./pages/UploadFile";
import Terms from "./pages/Terms";
import Privacy from "./pages/Privacy";
import Unsubscribe from "./pages/Unsubscribe";
import ForgotPassword from "./pages/ForgotPassword";
import ResetPassword from "./pages/ResetPassword";
import ProcessJob from "./pages/ProcessJob";
import ViewJobData from "./pages/ViewJobData";
import Results from "./pages/Results";
import ResultsRecords from "./pages/ResultsRecords";
import ResultsDetail from "./pages/ResultsDetail";
import Team from "./pages/Team";
import Profile from "./pages/Profile";
import AcceptInvite from "./pages/AcceptInvite";
import Billing from "./pages/Billing";
import MasterFile from "./pages/MasterFile";
import MasterFileRecords from "./pages/MasterFileRecords";
import MasterFileDuplicates from "./pages/MasterFileDuplicates";

// Admin pages
import AdminLogin from "./pages/admin/Login";
import AdminVerify2FA from "./pages/admin/Verify2FA";
import AdminDashboard from "./pages/admin/Dashboard";
import AdminTenants from "./pages/admin/Tenants";
import AdminTenantDetail from "./pages/admin/TenantDetail";
import AdminRevenue from "./pages/admin/Revenue";
import AdminDiscounts from "./pages/admin/Discounts";
import AdminEmailTemplates from "./pages/admin/EmailTemplates";
import AdminUsers from "./pages/admin/AdminUsers";
import AdminLoggedOut from "./pages/admin/LoggedOut";
import AdminJobRecords from "./pages/admin/JobRecords";
import AdminJobMetrics from "./pages/admin/JobMetrics";
import AdminJobExecution from "./pages/admin/JobExecution";
import AdminEnrichmentAudit from "./pages/admin/EnrichmentAudit";
import AdminVisitors from "./pages/admin/Visitors";
import AdminJobs from "./pages/admin/Jobs";
import AdminActivity from "./pages/admin/Activity";
import AdminSecurity from "./pages/admin/Security";
import AdminSurveys from "./pages/admin/Surveys";
import AdminSurveyResults from "./pages/admin/SurveyResults";
import AdminSurveyPreview from "./pages/admin/SurveyPreview";
import AdminFinn from "./pages/admin/Finn";
import AdminDash from "./pages/admin/Dash";
import DashAnalytics from "./pages/admin/DashAnalytics";
import AdminPageMonitor from "./pages/admin/PageMonitor";
import AdminOpsAI from "./pages/admin/OpsAI";
import AdminChat from "./pages/admin/Chat";
import AiEngine from "./pages/admin/AiEngine";
import AdminApiKeys from "./pages/admin/ApiKeys";
import AdminPartners from "./pages/admin/Partners";
import AdminChangePassword from "./pages/admin/ChangePassword";
import SurveyPage from "./pages/SurveyPage";

// B2B Prospecting pages
import B2BAccounts from "./pages/admin/b2b/Accounts";
import B2BAccountDetail from "./pages/admin/b2b/AccountDetail";
import B2BContacts from "./pages/admin/b2b/Contacts";
import B2BContactDetail from "./pages/admin/b2b/ContactDetail";
import B2BLists from "./pages/admin/b2b/Lists";
import B2BListDetail from "./pages/admin/b2b/ListDetail";
import B2BMarketingTemplates from "./pages/admin/b2b/MarketingTemplates";
import B2BStreams from "./pages/admin/b2b/Streams";
import B2BMarketingJobs from "./pages/admin/b2b/MarketingJobs";
import B2BMarketingJobDetail from "./pages/admin/b2b/MarketingJobDetail";
import B2BApolloImport from "./pages/admin/b2b/ApolloImport";
import B2BApolloPull from "./pages/admin/b2b/ApolloPull";
import B2BListBuilder from "./pages/admin/b2b/ListBuilder";
import B2BTitleClassification from "./pages/admin/b2b/TitleClassification";
import B2BTemplateBuilder from "./pages/admin/b2b/TemplateBuilder";

export default function App() {
  return (
    <BrowserRouter>
      <Routes>
        {/* Client routes */}
        <Route path="/login" element={<Login />} />
        <Route path="/register" element={<Register />} />
        <Route path="/walkthrough" element={<Walkthrough />} />
        <Route path="/verify-2fa" element={<Verify2FA />} />
        <Route path="/verify-email" element={<VerifyEmail />} />
        <Route path="/accept-invite" element={<AcceptInvite />} />
        <Route path="/dashboard" element={<RequireAuth><Dashboard /></RequireAuth>} />
        <Route path="/upload" element={<RequireAuth><UploadFile /></RequireAuth>} />
        <Route path="/billing" element={<RequireAuth><Billing /></RequireAuth>} />
        <Route path="/master-file" element={<RequireAuth><MasterFile /></RequireAuth>} />
        <Route path="/master-file/records" element={<RequireAuth><MasterFileRecords /></RequireAuth>} />
        <Route path="/master-file/duplicates" element={<RequireAuth><MasterFileDuplicates /></RequireAuth>} />
        <Route path="/team" element={<RequireAuth><Team /></RequireAuth>} />
        <Route path="/profile" element={<RequireAuth><Profile /></RequireAuth>} />
        <Route path="/process/:jobId" element={<RequireAuth><ProcessJob /></RequireAuth>} />
        <Route path="/jobs/:jobId/data" element={<RequireAuth><ViewJobData /></RequireAuth>} />
        <Route path="/results/:jobId" element={<RequireAuth><Results /></RequireAuth>} />
        <Route path="/results/:jobId/records" element={<RequireAuth><ResultsRecords /></RequireAuth>} />
        <Route path="/results/:jobId/records/:rowNumber" element={<RequireAuth><ResultsDetail /></RequireAuth>} />
        <Route path="/" element={<Navigate to="/dashboard" replace />} />
        <Route path="/forgot-password" element={<ForgotPassword />} />
        <Route path="/reset-password" element={<ResetPassword />} />
        <Route path="/terms" element={<Terms />} />
        <Route path="/privacy" element={<Privacy />} />
        <Route path="/unsubscribe" element={<Unsubscribe />} />

        {/* Admin routes */}
        <Route path="/admin/login" element={<AdminLogin />} />
        <Route path="/admin/logged-out" element={<AdminLoggedOut />} />
        <Route path="/admin/verify-2fa" element={<AdminVerify2FA />} />
        <Route path="/admin/dashboard" element={<RequireAdmin><AdminDashboard /></RequireAdmin>} />
        <Route path="/admin/tenants" element={<RequireAdmin><AdminTenants /></RequireAdmin>} />
        <Route path="/admin/tenants/:tenantId" element={<RequireAdmin><AdminTenantDetail /></RequireAdmin>} />
        <Route path="/admin/tenants/:tenantId/jobs/:jobId/records" element={<RequireAdmin><AdminJobRecords /></RequireAdmin>} />
        <Route path="/admin/revenue" element={<RequireAdmin><AdminRevenue /></RequireAdmin>} />
        <Route path="/admin/discounts" element={<RequireAdmin><AdminDiscounts /></RequireAdmin>} />
        <Route path="/admin/email-templates" element={<RequireAdmin><AdminEmailTemplates /></RequireAdmin>} />
        <Route path="/admin/users" element={<RequireAdmin><AdminUsers /></RequireAdmin>} />
        <Route path="/admin/visitors" element={<RequireAdmin><AdminVisitors /></RequireAdmin>} />
        <Route path="/admin/jobs" element={<RequireAdmin><AdminJobs /></RequireAdmin>} />
        <Route path="/admin/jobs/:jobId/metrics" element={<RequireAdmin><AdminJobMetrics /></RequireAdmin>} />
        <Route path="/admin/jobs/:jobId/execution" element={<RequireAdmin><AdminJobExecution /></RequireAdmin>} />
        <Route path="/admin/enrichment-audit" element={<RequireAdmin><AdminEnrichmentAudit /></RequireAdmin>} />
        <Route path="/admin/activity" element={<RequireAdmin><AdminActivity /></RequireAdmin>} />
        <Route path="/admin/security" element={<RequireAdmin><AdminSecurity /></RequireAdmin>} />
        <Route path="/admin/surveys" element={<RequireAdmin><AdminSurveys /></RequireAdmin>} />
        <Route path="/admin/surveys/:id/results" element={<RequireAdmin><AdminSurveyResults /></RequireAdmin>} />
        <Route path="/admin/surveys/:id/preview" element={<RequireAdmin><AdminSurveyPreview /></RequireAdmin>} />
        <Route path="/admin/finn" element={<RequireAdmin><AdminFinn /></RequireAdmin>} />
        <Route path="/admin/dash"    element={<RequireAdmin><AdminDash /></RequireAdmin>} />
        <Route path="/admin/dash/analytics" element={<RequireAdmin><DashAnalytics /></RequireAdmin>} />
        <Route path="/admin/monitor" element={<RequireAdmin><AdminPageMonitor /></RequireAdmin>} />
        <Route path="/admin/ops-ai" element={<RequireAdmin><AdminOpsAI /></RequireAdmin>} />
        <Route path="/admin/chat" element={<RequireAdmin><AdminChat /></RequireAdmin>} />
        <Route path="/admin/ai-engine" element={<RequireAdmin><AiEngine /></RequireAdmin>} />
        <Route path="/admin/api-keys" element={<RequireAdmin><AdminApiKeys /></RequireAdmin>} />
        <Route path="/admin/partners" element={<RequireAdmin><AdminPartners /></RequireAdmin>} />
        <Route path="/admin/change-password" element={<RequireAdmin><AdminChangePassword /></RequireAdmin>} />
        <Route path="/admin" element={<Navigate to="/admin/dashboard" replace />} />

        {/* Public survey — no auth */}
        <Route path="/survey/:token" element={<SurveyPage />} />

        {/* B2B Prospecting routes */}
        <Route path="/admin/b2b/accounts" element={<RequireAdmin><B2BAccounts /></RequireAdmin>} />
        <Route path="/admin/b2b/accounts/:id" element={<RequireAdmin><B2BAccountDetail /></RequireAdmin>} />
        <Route path="/admin/b2b/contacts" element={<RequireAdmin><B2BContacts /></RequireAdmin>} />
        <Route path="/admin/b2b/contacts/:id" element={<RequireAdmin><B2BContactDetail /></RequireAdmin>} />
        <Route path="/admin/b2b/lists" element={<RequireAdmin><B2BLists /></RequireAdmin>} />
        <Route path="/admin/b2b/lists/builder" element={<RequireAdmin><B2BListBuilder /></RequireAdmin>} />
        <Route path="/admin/b2b/lists/:id" element={<RequireAdmin><B2BListDetail /></RequireAdmin>} />
        <Route path="/admin/b2b/templates" element={<RequireAdmin><B2BMarketingTemplates /></RequireAdmin>} />
        <Route path="/admin/b2b/streams" element={<RequireAdmin><B2BStreams /></RequireAdmin>} />
        <Route path="/admin/b2b/jobs" element={<RequireAdmin><B2BMarketingJobs /></RequireAdmin>} />
        <Route path="/admin/b2b/jobs/:id" element={<RequireAdmin><B2BMarketingJobDetail /></RequireAdmin>} />
        <Route path="/admin/b2b/import/apollo" element={<RequireAdmin><B2BApolloImport /></RequireAdmin>} />
        <Route path="/admin/b2b/apollo-pull" element={<RequireAdmin><B2BApolloPull /></RequireAdmin>} />
        <Route path="/admin/b2b/classify" element={<RequireAdmin><B2BTitleClassification /></RequireAdmin>} />
        <Route path="/admin/b2b/builder" element={<RequireAdmin><B2BTemplateBuilder /></RequireAdmin>} />

        <Route path="*" element={<Navigate to="/dashboard" replace />} />
      </Routes>
    </BrowserRouter>
  );
}
